/**
 * Flutterwave Error Classifier & Logger
 *
 * Classifies Flutterwave API error messages into three categories so the UI
 * can decide what (if anything) to show the user.
 *
 * ┌────────────────────────────┬────────────────────────────────────────────┐
 * │ Category                   │ UI Behaviour                              │
 * ├────────────────────────────┼────────────────────────────────────────────┤
 * │ customer_error             │ Show the original Flutterwave message     │
 * │ gateway_error              │ Show a generic "temporarily unavailable"  │
 * │ merchant_restriction       │ Hide FLW message; show neutral fallback   │
 * └────────────────────────────┴────────────────────────────────────────────┘
 */

// ── Types ──────────────────────────────────────────────────────────────────

export type FlutterwaveErrorCategory =
  | "customer_error"
  | "gateway_error"
  | "merchant_restriction";

export interface ClassifiedError {
  /** Which bucket the error falls into */
  category: FlutterwaveErrorCategory;
  /** The message that should actually be rendered in the UI */
  userMessage: string;
  /** The raw Flutterwave message (for logging; never show merchant_restriction to user) */
  rawMessage: string;
}

export interface PaymentErrorLog {
  gateway: "flutterwave";
  error: string;
  category: FlutterwaveErrorCategory;
  timestamp: Date;
  userId?: string;
  paymentMethod?: string;
}

// ── Pattern lists ──────────────────────────────────────────────────────────

/**
 * Customer / bank-side errors — safe to surface verbatim.
 * These originate from the user's bank or MoMo provider.
 */
const CUSTOMER_ERROR_PATTERNS: string[] = [
  "insufficient funds",
  "invalid account",
  "incorrect otp",
  "incorrect pin",
  "wrong pin",
  "daily transaction limit",
  "transaction limit",
  "transaction declined",
  "declined",
  "invalid card",
  "expired card",
  "card expired",
  "do not honor",
  "do not honour",
  "invalid pin",
  "pin tries exceeded",
  "payer not found",
  "no response from beneficiary",
  "not enough balance",
  "exceeds withdrawal",
  "invalid amount",
];

/**
 * Temporary gateway / infrastructure errors — show a generic message.
 * These are transient problems on Flutterwave's side or network.
 */
const GATEWAY_ERROR_PATTERNS: string[] = [
  "gateway timeout",
  "server error",
  "communication timeout",
  "timeout",
  "payment temporarily unavailable",
  "temporarily unavailable",
  "service unavailable",
  "connection refused",
  "connection reset",
  "enotfound",
  "econnrefused",
  "socket hang up",
  "internal server error",
  "bad gateway",
  "503",
  "502",
];

/**
 * Merchant-level restrictions — NEVER show the raw message to the user.
 * Log internally for dev investigation.
 */
const MERCHANT_RESTRICTION_PATTERNS: string[] = [
  "irregular transaction pattern",
  "under review",
  "merchant restricted",
  "account review",
  "merchant not allowed",
  "merchant not enabled",
  "not enabled for",
  "compliance",
  "risk review",
  "flagged",
  "suspended",
  "blacklist",
];

// ── User-facing message templates ──────────────────────────────────────────

const USER_MESSAGES: Record<FlutterwaveErrorCategory, string> = {
  customer_error: "", // filled dynamically with raw message
  gateway_error:
    "This payment option is temporarily unavailable. Please try another payment method.",
  merchant_restriction:
    "This payment option is currently unavailable.",
};

// ── Core classifier ────────────────────────────────────────────────────────

/**
 * Classify a raw Flutterwave error message into one of three categories.
 *
 * Priority order (first match wins):
 *  1. merchant_restriction  — must be hidden from user
 *  2. customer_error        — safe to show
 *  3. gateway_error         — fallback for anything unrecognised
 */
export function classifyFlutterwaveError(errorMessage: string): FlutterwaveErrorCategory {
  if (!errorMessage) return "gateway_error";

  const lower = errorMessage.toLowerCase();

  // 1) Merchant / account restriction — highest priority so it's never leaked
  for (const pattern of MERCHANT_RESTRICTION_PATTERNS) {
    if (lower.includes(pattern)) return "merchant_restriction";
  }

  // 2) Customer / bank errors — safe to surface
  for (const pattern of CUSTOMER_ERROR_PATTERNS) {
    if (lower.includes(pattern)) return "customer_error";
  }

  // 3) Gateway / infra errors
  for (const pattern of GATEWAY_ERROR_PATTERNS) {
    if (lower.includes(pattern)) return "gateway_error";
  }

  // Default: treat unknown errors as gateway errors (safe generic message)
  return "gateway_error";
}

// ── High-level helper: classify + produce user message ─────────────────────

/**
 * Classify a Flutterwave error and return both the category and the
 * message that should be displayed to the user.
 */
export function getClassifiedError(rawMessage: string): ClassifiedError {
  const category = classifyFlutterwaveError(rawMessage);

  let userMessage: string;
  if (category === "customer_error") {
    // Show the actual bank / provider message
    userMessage = rawMessage;
  } else {
    userMessage = USER_MESSAGES[category];
  }

  return { category, userMessage, rawMessage };
}

// ── Structured logger ──────────────────────────────────────────────────────

/**
 * Log only operational classification metadata. Provider error bodies can
 * contain customer or payment data and must not be copied into application logs.
 */
export function logPaymentError(entry: PaymentErrorLog): void {
  const prefix = `[flw-error][${entry.category}]`;
  const meta = [
    `gateway=${entry.gateway}`,
    entry.paymentMethod ? `method=${entry.paymentMethod}` : null,
    `time=${entry.timestamp.toISOString()}`,
  ]
    .filter(Boolean)
    .join(", ");

  if (entry.category === "merchant_restriction") {
    // Always log at error level so it's visible in monitoring
    console.error(`${prefix} MERCHANT RESTRICTION — ${meta}`);
  } else {
    console.warn(`${prefix} ${meta}`);
  }
}
